import { SubmitHelpers } from '@area2k/use-form';
import useModal from '@area2k/use-modal';

import { isEqual } from 'lodash';
import { useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import ConfirmationModal from '@/components/ConfirmationModal';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Option from '@/components/Option';
import Stack from '@/components/Stack';
import Form from '@/form';
import FormColumns from '@/form/FormColumns';
import MaskedInputField from '@/form/MaskedInputField';
import TextAreaField from '@/form/TextAreaField';
import TextField from '@/form/TextField';
import TextSelectField from '@/form/TextSelectField';
import { useWorkerDisableMutation, useWorkerEnableMutation } from '@/graphql';
import {
  GenderEnum,
  GetWorkerQuery,
  LanguageEnum,
  SmartphoneTypeEnum,
  TaxTypeEnum,
} from '@/types/graphql';
import {
  WorkersV2API,
  UpdateWorkerProfilePayload,
} from '@/services/workersV2API';
import { CONFIRM_MSG } from '@/util/constants';

import EmailTakenAlert, {
  ConflictingWorker,
} from '@/routes/Agency/WorkerV2/components/EmailTakenAlert';

const genderEnumToDb: Record<GenderEnum, string> = {
  [GenderEnum.MALE]: 'M',
  [GenderEnum.FEMALE]: 'F',
  [GenderEnum.NON_BINARY]: 'X',
  [GenderEnum.UNSPECIFIED]: 'U',
};

const languageEnumToDb: Record<LanguageEnum, string> = {
  [LanguageEnum.ENGLISH]: 'en',
  [LanguageEnum.SPANISH]: 'es',
};

const smartphoneEnumToDb: Record<SmartphoneTypeEnum, string> = {
  [SmartphoneTypeEnum.ANDROID]: 'android',
  [SmartphoneTypeEnum.IOS]: 'ios',
  [SmartphoneTypeEnum.OTHER]: 'other',
};

export type Props = {
  worker: GetWorkerQuery['worker'];
  hideModal: () => void;
};

type FormValues = {
  active: Props['worker']['active'];
  email: Props['worker']['user']['email'];
  firstName: Props['worker']['user']['firstName'];
  middleName: Props['worker']['user']['middleName'];
  lastName: Props['worker']['user']['lastName'];
  gender: GenderEnum;
  heardFrom: Props['worker']['heardFrom'];
  language: LanguageEnum;
  phoneNumber: Props['worker']['user']['phoneNumber'];
  ownsCar: Props['worker']['ownsCar'];
  secondaryLanguage: LanguageEnum;
  smartphoneType: SmartphoneTypeEnum;
  dateOfBirth: Props['worker']['user']['dateOfBirth'];
  hasSsn: Props['worker']['hasSsn'];

  disableReason: Props['worker']['disableReason'];
  deactivatedByUser: Props['worker']['deactivatedByUser'];
};

const smartPhoneTypeOptions = [
  { label: 'Android', value: SmartphoneTypeEnum.ANDROID },
  { label: 'IOS', value: SmartphoneTypeEnum.IOS },
  { label: 'Other', value: SmartphoneTypeEnum.OTHER },
];

const genderOptions = [
  { label: 'Male', value: GenderEnum.MALE },
  { label: 'Female', value: GenderEnum.FEMALE },
  { label: 'Non-Binary', value: GenderEnum.NON_BINARY },
  { label: 'Unspecified', value: GenderEnum.UNSPECIFIED },
];

const UpdateProfileModal = ({ worker, hideModal }: Props) => {
  const initialValues: FormValues = {
    active: worker.active,
    email: worker.user.email,
    firstName: worker.user.firstName,
    middleName: worker.user.middleName,
    lastName: worker.user.lastName,
    gender: worker.gender,
    heardFrom: worker.heardFrom,
    dateOfBirth: new Date(worker.user.dateOfBirth!).toISOString().split('T')[0],
    language: worker.user.language,
    phoneNumber: worker.user.phoneNumber,
    ownsCar: worker.ownsCar ? worker.ownsCar : false,
    secondaryLanguage: worker.user.secondaryLanguage!,
    smartphoneType: worker.user.smartphoneType || SmartphoneTypeEnum.ANDROID,
    hasSsn: worker.hasSsn,

    disableReason: worker.disableReason,
    deactivatedByUser: worker.deactivatedByUser,
  };
  const [workerValues, setWorkerValues] = useState<FormValues>(initialValues);
  const [loading, setLoading] = useState(false);
  const [disableWorker] = useWorkerDisableMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(worker),
        fields: {
          status() {},
        },
      });
    },
  });
  const [enableWorker] = useWorkerEnableMutation({
    variables: {
      workerId: worker.id,
    },
    update: (cache) => {
      cache.modify({
        id: cache.identify(worker),
        fields: {
          status() {},
        },
      });
    },
  });
  const [emailTaken, setEmailTaken] = useState<{
    message: string;
    conflictingWorker: ConflictingWorker;
  } | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const handleWorkerValuesChange = ({ value }, id: keyof FormValues) =>
    setWorkerValues({ ...workerValues, [id]: value });
  const [showConfirmationModal, hideConfirmationModal] = useModal(
    ({ values, submitHelpers }: any) => (
      <ConfirmationModal
        acceptAction={() => handleSubmit(values, submitHelpers)}
        acceptButtonLabel="Confirm"
        bodyContentText={CONFIRM_MSG}
        denyAction={() => {}}
        denyButtonLabel="Cancel"
        hideModal={hideConfirmationModal}
        title={`Confirmation`}
        loading={loading}
      />
    ),
    [workerValues, initialValues],
  );

  const handleSubmit = async (
    values: FormValues,
    _submitHelpers: SubmitHelpers,
  ) => {
    setLoading(true);
    setEmailTaken(null);
    setGenericError(null);
    try {
      if (isEqual(workerValues, initialValues)) {
        return hideModal();
      }

      const {
        dateOfBirth,
        email,
        firstName,
        middleName,
        lastName,
        gender,
        heardFrom,
        language,
        phoneNumber,
        secondaryLanguage,
        smartphoneType,
        disableReason,
      } = values;

      const { active, ownsCar, hasSsn } = workerValues;

      if (!active) {
        await disableWorker({
          variables: {
            workerId: worker.id,
            reenableDate: null,
            reason: disableReason || null,
          },
        });
      } else {
        await enableWorker();
      }

      const payload: UpdateWorkerProfilePayload = {
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        email,
        date_of_birth: dateOfBirth!,
        gender: gender ? genderEnumToDb[gender] : null,
        language: language ? languageEnumToDb[language] : null,
        secondary_language: secondaryLanguage
          ? languageEnumToDb[secondaryLanguage]
          : null,
        smartphone_type: smartphoneType
          ? smartphoneEnumToDb[smartphoneType]
          : null,
        heard_from: heardFrom,
        owns_car: ownsCar,
        has_ssn: hasSsn,
        phone_number: phoneNumber,
      };

      const result = await WorkersV2API.updateWorkerProfile(
        Number(worker.id),
        payload,
      );

      if (result.success) {
        hideModal();
        return;
      }

      const taken = result.errors.find((e) => e.code === 'taken');
      if (taken) {
        setEmailTaken({
          message: taken.message,
          conflictingWorker: taken.conflicting_worker ?? null,
        });
      } else {
        setGenericError(
          result.errors[0]?.message ?? 'Failed to update profile.',
        );
      }
    } catch (err) {
      setGenericError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="lg"
      title="Update Profile"
      onRequestClose={hideModal}
    >
      <Card.Section>
        {emailTaken && (
          <EmailTakenAlert
            message={emailTaken.message}
            conflictingWorker={emailTaken.conflictingWorker}
          />
        )}
        {genericError && !emailTaken && (
          <EmailTakenAlert message={genericError} conflictingWorker={null} />
        )}
        <Form
          initialValues={initialValues}
          onSubmit={async (values: FormValues, submitHelpers: SubmitHelpers) =>
            showConfirmationModal({ values, submitHelpers })
          }
        >
          <FormColumns layout="triple">
            <TextField
              autoFocus
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'firstName');
              }}
              fieldId="firstName"
              label="First Name"
            />
            <TextField
              autoFocus
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'middleName');
              }}
              fieldId="middleName"
              label="Middle Name"
            />
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'lastName');
              }}
              fieldId="lastName"
              label="Last Name"
            />
          </FormColumns>
          <FormColumns>
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'dateOfBirth');
              }}
              fieldId="dateOfBirth"
              label="Date of Birth"
              type="date"
            />
            <TextSelectField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'gender');
              }}
              fieldId="gender"
              label="Gender"
              options={genderOptions}
            />
          </FormColumns>
          <FormColumns>
            <MaskedInputField
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'phoneNumber');
              }}
              fieldId="phoneNumber"
              incompletemessage="Must be a valid phone number"
              label="Phone Number"
              mask="(000) 000-0000"
              placeholder={
                workerValues.phoneNumber
                  ? workerValues.phoneNumber
                  : '(555) 555-5555'
              }
              type="tel"
            />
            <TextField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'email');
              }}
              fieldId="email"
              label="Email Address"
            />
          </FormColumns>
          <FormColumns>
            <TextSelectField
              required
              callback={(fieldContext) => {
                handleWorkerValuesChange(fieldContext, 'smartphoneType');
              }}
              fieldId="smartphoneType"
              label="Phone Type"
              options={smartPhoneTypeOptions}
            />
            <FormElement label="Important Information">
              <Stack vertical>
                <Option
                  appearance="checkbox"
                  checked={
                    worker.taxType === TaxTypeEnum.TAX_W2
                      ? false
                      : workerValues.hasSsn
                  }
                  disabled={worker.taxType === TaxTypeEnum.TAX_W2}
                  id="hasSsn"
                  label="Eligible for W2 employment"
                  onChange={() =>
                    handleWorkerValuesChange(
                      { value: !workerValues.hasSsn },
                      'hasSsn',
                    )
                  }
                />
                <Option
                  appearance="checkbox"
                  checked={workerValues.ownsCar}
                  id="ownsCar"
                  label="Access To Reliable Transportation?"
                  onChange={() =>
                    handleWorkerValuesChange(
                      { value: !workerValues.ownsCar },
                      'ownsCar',
                    )
                  }
                />
              </Stack>
            </FormElement>
          </FormColumns>
          <FormColumns>
            <FormElement label="Status">
              <span style={{ fontSize: '14px' }}>
                {workerValues.active ? 'Enabled' : 'Disabled'}
                {!workerValues.active && workerValues.deactivatedByUser && (
                  <>
                    {' — disabled by '}
                    <span style={{ fontWeight: 'bold' }}>
                      {[
                        workerValues.deactivatedByUser.firstName,
                        workerValues.deactivatedByUser.middleName,
                        workerValues.deactivatedByUser.lastName,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    </span>
                  </>
                )}
              </span>
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Use the V2 profile to change account status
              </span>
            </FormElement>
          </FormColumns>
          <Stack justify="end">
            <Button
              a11yLabel="Submit form"
              id="btn-submit"
              isLoading={loading}
              label="Save"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default UpdateProfileModal;
